import * as Yup from "yup";
import moment from "moment";

import { FieldNameEnum } from "./FieldNameEnum";
import { AccountTypeEnum } from "./AccountTypeEnum";
import { OtherEmployerAccountType } from "./OtherEmployerAccountTypeArray";
import { TransferInstructionsEnum } from "./TransferInstructionsEnum";

const ssnRegExp = /^(\d{3}-?\d{2}-?\d{4}|XXX-XX-XXXX)$/;
const emailRegExp = /^(?:(?!adp\.com).)*$/;

export const rttFieldSchema = Yup.object().shape({
  agreementId: Yup.string().required(),
  currentFirm: Yup.string()
    .max(150, "This value cannot be more than 150 characters.")
    .required("This field is required."),
  currentAccount: Yup.string()
    .max(100, "This value cannot be more than 100 characters.")
    .required("This field is required."),
  currentAccountType: Yup.string().required("This field is required."),
  initialFundingDate: Yup.string().when(FieldNameEnum.currentAccountType, {
    is: (value: AccountTypeEnum) => value === AccountTypeEnum.SimpleIRA,
    then: Yup.string()
      .required("This field is required.")
      .test(
        "date",
        "This value is not properly formatted (ex. MM/DD/YYYY).",
        value => moment(value, "MM/DD/YYYY").format("MM/DD/YYYY") === value
      )
      .test("date", "This value cannot be in the future.", value =>
        moment(value, "MM/DD/YYYY").isBefore(moment())
      ),
    otherwise: Yup.string().notRequired()
  }),
  rothYear: Yup.string().when(FieldNameEnum.currentAccountType, {
    is: (value: AccountTypeEnum) => value === AccountTypeEnum.RothIRA,
    then: Yup.string()
      .required("This field is required.")
      .test(
        "date",
        "This value is not properly formatted (ex. YYYY).",
        value => moment(value, "YYYY").format("YYYY") === value
      )
      .test("date", "This value cannot be in the future.", value =>
        moment(value, "YYYY").isBefore(moment())
      ),
    otherwise: Yup.string().notRequired()
  }),
  toAccountType: Yup.string().required(),
  rothPortion: Yup.string().when(FieldNameEnum.currentAccountType, {
    is: (currentAccount: AccountTypeEnum) =>
      OtherEmployerAccountType.includes(currentAccount),
    then: Yup.string().required("This field is required."),
    otherwise: Yup.string().notRequired()
  }),
  transferDollarEstimate: Yup.number().when(
    [FieldNameEnum.transferInstructions],
    {
      is: (transferInstructions: TransferInstructionsEnum) => {
        return transferInstructions === TransferInstructionsEnum.All;
      },
      then: Yup.number()
        .required("This field is required.")
        .min(1, "This value must be greater than 1."),
      otherwise: Yup.number().notRequired()
    }
  ),
  transferDollarAmount: Yup.number().when(
    [FieldNameEnum.transferInstructions],
    {
      is: (transferInstructions: TransferInstructionsEnum) =>
        transferInstructions === TransferInstructionsEnum.Dollar,
      then: Yup.number()
        .required("This field is required.")
        .min(1, "This value must be greater than 1."),
      otherwise: Yup.number().notRequired()
    }
  ),
  transferPercent: Yup.string().when(["transferInstructions"], {
    is: (transferInstructions: TransferInstructionsEnum) =>
      transferInstructions === TransferInstructionsEnum.Percent,
    then: Yup.string()
      .min(1, "Please enter a percent between 1 and 99.")
      .max(99, "Please enter a percent between 1 and 99.")
      .required("This field is required."),
    otherwise: Yup.string().notRequired()
  }),
  firstName: Yup.string()
    .required("This field is required.")
    .max(35, "This value cannot be more than 35 characters."),
  lastName: Yup.string()
    .required("This field is required.")
    .max(35, "This value cannot be more than 35 characters."),
  birthDate: Yup.string()
    .required("This field is required.")
    .test(
      "date",
      "This value is not properly formatted (ex. MM/DD/YYYY).",
      value => moment(value, "MM/DD/YYYY").format("MM/DD/YYYY") === value
    )
    .test("date", "This value cannot be in the future.", value =>
      moment(value, "MM/DD/YYYY").isBefore(moment())
    ),
  ssn: Yup.string()
    .required("This field is required.")
    .matches(
      ssnRegExp,
      "This value is not properly formatted (ex. XXX-XX-XXXX)."
    ),
  email: Yup.string()
    .required("This field is required.")
    .max(50, "This value cannot be more than 50 characters.")
    .email(
      "Email address entered is not properly formatted (ex. johndoe@domain.com)."
    )
    .matches(
      emailRegExp,
      "Email may not contain @adp.com and the email must be properly formatted."
    ),
  primaryPhone: Yup.string()
    .required("This field is required.")
    .matches(
      /^[(]?\d{3}[)]?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      "This value must be 10 numeric digits."
    ),
  secondaryPhone: Yup.string()
    .matches(
      /^[(]?\d{3}[)]?[\s.-]?\d{3}[\s.-]?\d{4}$/,
      "This value must be 10 numeric digits."
    )
    .notRequired(),
  transferToSame: Yup.string().required(),
  byo: Yup.string().required()
});
